/**
 * Model Validator Utility
 * 
 * Validates and converts Gemini model names to ensure they are valid
 * and not deprecated/preview models that could cause 404 errors.
 */

// List of valid stable Gemini models
export const VALID_MODELS = [
    'gemini-2.5-flash-lite', // Latest economy model with enhanced reasoning
    'gemini-2.0-flash',
    'gemini-2.0-flash-lite', 
    'gemini-1.5-flash',
    'gemini-1.5-pro'
] as const;

export type ValidModel = typeof VALID_MODELS[number];

// Default fallback model
export const DEFAULT_MODEL: ValidModel = 'gemini-2.5-flash-lite';

/**
 * Validates a model name and returns a valid model.
 * Converts deprecated/preview models to the default stable model.
 * 
 * @param model - The model name to validate
 * @returns A valid model name
 */
export function validateModel(model: string | undefined | null): ValidModel {
    // No model provided - use default
    if (!model) {
        return DEFAULT_MODEL;
    }
    
    // Check if model is in the valid list
    if (VALID_MODELS.includes(model as ValidModel)) {
        return model as ValidModel;
    }
    
    // Model is invalid (likely preview/experimental/deprecated)
    // Log warning and return default
    const invalidReasons: string[] = [];
    
    if (model.includes('preview')) {
        invalidReasons.push('preview model');
    }
    if (model.includes('exp')) {
        invalidReasons.push('experimental model');
    }
    if (model.includes('2.5')) {
        invalidReasons.push('version 2.5 not available');
    }
    
    console.warn(
        `[ModelValidator] Invalid model "${model}" (${invalidReasons.join(', ') || 'not in valid list'}) → using ${DEFAULT_MODEL}`
    );
    
    return DEFAULT_MODEL;
}

/**
 * Checks if a model name is valid without conversion.
 * 
 * @param model - The model name to check
 * @returns True if the model is valid
 */
export function isValidModel(model: string | undefined | null): model is ValidModel {
    return !!model && VALID_MODELS.includes(model as ValidModel);
}

/**
 * Gets the appropriate model based on user premium status.
 * Validates both models before returning.
 * 
 * @param isPremium - Whether the user has premium access
 * @param freeModel - The free tier model from config
 * @param premiumModel - The premium tier model from config
 * @returns A valid model name
 */
export function getValidatedModelForUser(
    isPremium: boolean,
    freeModel?: string,
    premiumModel?: string
): ValidModel {
    const targetModel = isPremium ? premiumModel : freeModel;
    return validateModel(targetModel);
}

